import { useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { LithuanianDatePicker } from '@/components/ui/lithuanian-date-picker';
import { ChevronDown, ChevronUp, Info, Sun, Moon, Sparkles, CalendarIcon, Zap, AlertTriangle, X } from 'lucide-react';
import { format } from 'date-fns';

// Lithuanian month names
const MONTHS_LT = [
  'Sausis', 'Vasaris', 'Kovas', 'Balandis', 'Gegužė', 'Birželis',
  'Liepa', 'Rugpjūtis', 'Rugsėjis', 'Spalis', 'Lapkritis', 'Gruodis'
];

// Lithuanian weekday names
const WEEKDAYS_LT = [
  'Sekmadienis', 'Pirmadienis', 'Antradienis', 'Trečiadienis',
  'Ketvirtadienis', 'Penktadienis', 'Šeštadienis'
];

// Reduce number to single digit (1-9)
const reduceToSingleDigit = (num) => {
  while (num > 9) {
    num = String(num).split('').reduce((sum, digit) => sum + parseInt(digit), 0);
  }
  return num;
};

// Calculate numbers
const calculateDayNumber = (day) => reduceToSingleDigit(day);
const calculateMonthNumber = (month) => reduceToSingleDigit(month);
const calculateYearNumber = (year) => {
  const sum = String(year).split('').reduce((s, d) => s + parseInt(d), 0);
  return reduceToSingleDigit(sum);
};

// Weekday planet data
const WEEKDAY_PLANETS = [
  { name: 'Saulė', genitive: 'Saulės', symbol: '☉', day: 'Sekmadienis', number: 1 },
  { name: 'Mėnulis', genitive: 'Mėnulio', symbol: '☽', day: 'Pirmadienis', number: 2 },
  { name: 'Marsas', genitive: 'Marso', symbol: '♂', day: 'Antradienis', number: 9 },
  { name: 'Merkurijus', genitive: 'Merkurijaus', symbol: '☿', day: 'Trečiadienis', number: 5 },
  { name: 'Jupiteris', genitive: 'Jupiterio', symbol: '♃', day: 'Ketvirtadienis', number: 3 },
  { name: 'Venera', genitive: 'Veneros', symbol: '♀', day: 'Penktadienis', number: 6 },
  { name: 'Saturnas', genitive: 'Saturno', symbol: '♄', day: 'Šeštadienis', number: 8 }
];

const getWeekdayPlanet = (date) => WEEKDAY_PLANETS[date.getDay()];

// Calculate shadow sum
const calculateShadowSum = (dayNum, monthNum, yearNum, planetNum) => {
  const sum = dayNum + monthNum + yearNum + planetNum;
  return reduceToSingleDigit(sum);
};

// =====================================================
// DIENOS SKAIČIAUS DUOMENYS (40% - pilni aprašymai)
// =====================================================
const DAY_NUMBER_DATA = {
  1: {
    title: 'Pradžios impulsas',
    short: 'Pradžia ir lyderystė',
    theme: 'naujos pradžios ir lyderystės',
    fullDescription: 'Skaičius 1 neša galingą iniciatyvos ir naujos pradžios energiją. Tai impulsas, skatinantis žengti pirmuosius žingsnius, imtis lyderystės ir pasitikėti savo unikalia vizija. Ši energija atveria drąsą veikti savarankiškai, kurti naujus projektus ir vadovauti – pirmiausia sau. Individualumas ir asmeninis autentiškumas šiandien yra stipriausiame taške.',
    shadow: 'Per didelis susitelkimas į save, nekantrumas, noras viską kontroliuoti. Svarbu prisiminti, kad tikra lyderystė yra tarnavimas, ne dominavimas.',
    activities: 'Nauji projektai, asmeniniai tikslai, lyderystės iniciatyvos, drąsūs sprendimai.'
  },
  2: {
    title: 'Partnerystės impulsas',
    short: 'Partnerystė ir jautrumas',
    theme: 'partnerystės ir jautrumo',
    fullDescription: 'Skaičius 2 skleidžia bendradarbiavimo ir jautrumo energiją. Tai impulsas, kviečiantis į diplomatiją, harmoniją ir gebėjimą įsiklausyti. Ši energija stiprina empatiją, intuiciją ir subtilų supratimą santykiuose. Partnerystė ir komandinis darbas šiandien yra palankiausi keliai į sėkmę.',
    shadow: 'Per didelis jautrumas, emocinė priklausomybė nuo kitų nuomonės, neapsisprendimas. Svarbu išlaikyti balansą tarp savęs ir kitų poreikių.',
    activities: 'Derybos, partnerystės pokalbiai, komandinis darbas, santykių stiprinimas.'
  },
  3: {
    title: 'Kūrybinis impulsas',
    short: 'Kūryba ir saviraiška',
    theme: 'kūrybos ir saviraiškos',
    fullDescription: 'Skaičius 3 pulsuoja kūrybine energija ir saviraiškos džiaugsmu. Tai impulsas, skatinantis leisti idėjoms tekėti, komunikuoti su entuziazmu ir dalintis savo unikaliu požiūriu. Ši energija neša lengvumą, optimizmą ir gebėjimą įkvėpti aplinkinius. Kūryba visomis formomis šiandien yra ypač palanki.',
    shadow: 'Išsiblaškymas, paviršutiniškumas, sunkumas užbaigti pradėtus darbus. Svarbu išlaikyti fokusą ir pasirinkti svarbiausias idėjas.',
    activities: 'Kūrybiniai projektai, rašymas, viešas kalbėjimas, socialiniai renginiai, mokymas.'
  },
  4: {
    title: 'Struktūros impulsas',
    short: 'Struktūra ir stabilumas',
    theme: 'struktūros ir stabilumo',
    fullDescription: 'Skaičius 4 neša tvarkos ir stabilumo energiją. Tai impulsas, kviečiantis kurti tvarų pagrindą, organizuoti ir sisteminti. Ši energija stiprina metodiškumą, atsakomybę ir gebėjimą matyti ilgalaikę perspektyvą. Praktiniai reikalai, planavimas ir pamatų statyba šiandien yra palankiausi.',
    shadow: 'Užsispyrimas, nelankstumas, per didelė kontrolė. Svarbu prisiminti, kad net tvirčiausi pamatai turi leisti kvėpuoti.',
    activities: 'Planavimas, finansų tvarkymas, namų organizavimas, sistemų kūrimas.'
  },
  5: {
    title: 'Pokyčių impulsas',
    short: 'Pokyčiai ir laisvė',
    theme: 'pokyčių ir laisvės',
    fullDescription: 'Skaičius 5 skleidžia judėjimo ir transformacijos energiją. Tai impulsas, skatinantis priimti pokyčius, tyrinėti nauja ir laužyti senas ribas. Ši energija neša nuotykių dvasią, lankstumą ir gebėjimą prisitaikyti. Kelionės, naujos patirtys ir laisvės ieškojimas šiandien yra ypač palankūs.',
    shadow: 'Nerimas, impulsyvumas, bėgimas nuo įsipareigojimų. Svarbu atskirti tikrą laisvę nuo pabėgimo.',
    activities: 'Kelionės, naujos pažintys, pokyčių inicijavimas, sportas, komunikacija.'
  },
  6: {
    title: 'Harmonijos impulsas',
    short: 'Harmonija ir meilė',
    theme: 'harmonijos ir meilės',
    fullDescription: 'Skaičius 6 spinduliuoja meilės ir rūpesčio energiją. Tai impulsas, kviečiantis kurti grožį, harmoniją ir rūpintis artimaisiais. Ši energija stiprina šeimynines vertybes, estetiką ir gebėjimą dalintis šiluma. Santykiai, namai ir grožio kūrimas šiandien yra centre.',
    shadow: 'Perfekcionizmas, savęs aukojimas, per didelis rūpestis kitais. Svarbu prisiminti, kad tikras rūpestis prasideda nuo savęs.',
    activities: 'Šeimos laikas, santykių stiprinimas, namų harmonizavimas, menas, SPA.'
  },
  7: {
    title: 'Refleksijos impulsas',
    short: 'Refleksija ir dvasingumas',
    theme: 'refleksijos ir dvasingumo',
    fullDescription: 'Skaičius 7 neša giluminio mąstymo ir dvasingumo energiją. Tai impulsas, kviečiantis į tylą, refleksiją ir vidinę kelionę. Ši energija stiprina intuiciją, analitinį mąstymą ir gebėjimą matyti tai, kas paslėpta. Meditacija, tyrimai ir dvasinės praktikos šiandien yra ypač veiksmingos.',
    shadow: 'Atsiribojimas, pesimizmas, per didelė analizė. Svarbu dalintis atradimais, o ne užsidaryti savyje.',
    activities: 'Meditacija, tyrimai, filosofiniai pokalbiai, rašymas, dvasinės praktikos.'
  },
  8: {
    title: 'Galios impulsas',
    short: 'Galia ir ambicijos',
    theme: 'galios ir ambicijų',
    fullDescription: 'Skaičius 8 telkia ambicijų ir materialinio valdymo energiją. Tai impulsas, skatinantis kurti ilgalaikę vertę, vadovauti ir daryti įtaką. Ši energija stiprina verslumo dvasią, autoritetą ir gebėjimą valdyti resursus. Verslo sprendimai, investicijos ir karjeros žingsniai šiandien yra palankūs.',
    shadow: 'Materializmas, kontrolės troškimas, dominavimas. Svarbu naudoti galią tarnaujant, ne valdant.',
    activities: 'Verslo derybos, investicijos, karjeros sprendimai, strateginis planavimas.'
  },
  9: {
    title: 'Išminties impulsas',
    short: 'Užbaigimas ir išminties',
    theme: 'užbaigimo ir išminties',
    fullDescription: 'Skaičius 9 neša užbaigimo ir universalios meilės energiją. Tai impulsas, kviečiantis paleisti tai, kas jau atliko savo vaidmenį, dalintis patirtimi ir tarnauti bendram gėriui. Ši energija stiprina humanizmą, užuojautą ir gebėjimą matyti plačiau. Atleidimas, išminties dalijimas ir ciklų užbaigimas šiandien yra centre. Baigiamosios pamokos, kurių ilgai mokytasi, gali būti sėkmingai integruotos.',
    shadow: 'Sentimentalumas, prisirišimas prie praeities, idealizmas. Svarbu paleisti su meile, o ne laikytis.',
    activities: 'Labdara, mentorystė, atleidimo praktikos, meno kūryba, dvasinės praktikos.'
  }
};

// =====================================================
// PLANETŲ DUOMENYS (40% - pilni aprašymai)
// =====================================================
const PLANET_FULL_DATA = {
  'Saulė': {
    title: 'Saulės režimas',
    short: 'Spindesys ir charizma',
    theme: 'spindesio ir charizmos',
    fullDescription: 'Saulės energija aktyvuoja vidinį centrą ir gebėjimą spindėti. Ji neša natūralią charizmą, kūrybinę galią ir lyderystės talentą. Ši planetinė įtaka stiprina individualizmą, saviraiškos drąsą ir gebėjimą vadovauti. Saulė kviečia būti matomiems, dalintis šviesa ir kurti tai, kas unikalu.',
    shadow: 'Egocentrizmas, puikybė, sunkumas priimti kritiką. Svarbu prisiminti, kad tikra šviesa nebijo šešėlių.',
    activities: 'Kūrybinė veikla, viešas kalbėjimas, asmeniniai projektai, vadovavimas.'
  },
  'Mėnulis': {
    title: 'Mėnulio režimas',
    short: 'Intuicija ir emocijos',
    theme: 'intuicijos ir emocijų',
    fullDescription: 'Mėnulio energija atveria emocinį suvokimą ir stiprina intuiciją. Ji neša gilų jautrumą, rūpestingumą ir gebėjimą jausti tai, ko žodžiai negali pasakyti. Ši planetinė įtaka stiprina emocinį intelektą, empatiją ir ryšį su vidine išmintimi. Mėnulis kviečia klausyti širdies ir puoselėti artimus ryšius.',
    shadow: 'Nuotaikų kaita, emocinė priklausomybė, per didelis jautrumas. Svarbu rasti balansą tarp jausmų ir proto.',
    activities: 'Šeimos laikas, emociniai pokalbiai, intuicijos lavinimas, meditacija.'
  },
  'Marsas': {
    title: 'Marso režimas',
    short: 'Veiksmas ir drąsa',
    theme: 'veiksmo ir drąsos',
    fullDescription: 'Marso energija aktyvuoja veiksmą, drąsą ir ryžtą. Ji neša fizinę jėgą, konkurencingumą ir gebėjimą veikti greitai. Ši planetinė įtaka stiprina valią, ambicijas ir drąsą siekti tikslų. Marsas kviečia veikti, ne laukti, ir ginti tai, kas svarbu.',
    shadow: 'Agresija, impulsyvumas, konfliktiškumas. Svarbu nukreipti energiją konstruktyviai ir žinoti, kada sustoti.',
    activities: 'Sportas, konkurencinės situacijos, derybos, fizinis darbas, drąsūs sprendimai.'
  },
  'Merkurijus': {
    title: 'Merkurijaus režimas',
    short: 'Komunikacija ir greitis',
    theme: 'komunikacijos ir greičio',
    fullDescription: 'Merkurijaus energija aktyvuoja mintis, žodžius ir ryšius. Ji neša greitą mąstymą, komunikacijos talentą ir mokymosi lengvumą. Ši planetinė įtaka stiprina lankstumą, prisitaikymą ir gebėjimą perduoti idėjas. Merkurijus kviečia kalbėti, rašyti, mokytis ir dalintis informacija.',
    shadow: 'Paviršutiniškumas, išsiblaškymas, per didelis greitis. Svarbu gilinti, ne tik skraidyti paviršiumi.',
    activities: 'Susitikimai, mokymasis, rašymas, trumpos kelionės, derybos.'
  },
  'Jupiteris': {
    title: 'Jupiterio režimas',
    short: 'Ekspansija ir sėkmė',
    theme: 'ekspansijos ir sėkmės',
    fullDescription: 'Jupiterio energija aktyvuoja augimą, optimizmą ir sėkmę. Ji neša gebėjimą matyti plačiau, dosnumą ir natūralų palankumą. Ši planetinė įtaka stiprina pasitikėjimą, išmintį ir galimybių matymą. Jupiteris kviečia galvoti dideliu mastu, plėstis ir tikėti sėkme.',
    shadow: 'Per didelis optimizmas, švaistymas, aklumas rizikoms. Svarbu derinti viziją su realybe.',
    activities: 'Verslo plėtra, kelionės, mokymasis, filosofiniai pokalbiai, investicijos.'
  },
  'Venera': {
    title: 'Veneros režimas',
    short: 'Meilė ir grožis',
    theme: 'meilės ir grožio',
    fullDescription: 'Veneros energija aktyvuoja meilę, grožį ir harmoniją. Ji neša estetinį jautrumą, socialinį žavesį ir gebėjimą mėgautis gyvenimu. Ši planetinė įtaka stiprina romantiškumą, meniškumą ir santykių harmoniją. Venera kviečia kurti grožį, mylėti ir leistis mylimai.',
    shadow: 'Hedonizmas, tingumas, per didelis dėmesys išorei. Svarbu rasti balansą tarp malonumo ir prasmės.',
    activities: 'Romantiški susitikimai, menas, SPA, socialiniai renginiai, grožio procedūros.'
  },
  'Saturnas': {
    title: 'Saturno režimas',
    short: 'Disciplina ir branda',
    theme: 'disciplinos ir brandos',
    fullDescription: 'Saturno energija aktyvuoja discipliną, atsakomybę ir brandą. Ji neša ištvermę, ilgalaikį mąstymą ir gebėjimą kurti tvarų pagrindą. Ši planetinė įtaka stiprina rimtumą, struktūrą ir karminio mokymosi procesą. Saturnas kviečia dirbti sistemingai, prisiimti atsakomybę ir kurti tai, kas išliks.',
    shadow: 'Pesimizmas, griežtumas, baimė. Svarbu prisiminti, kad disciplina ir džiaugsmas gali eiti kartu.',
    activities: 'Ilgalaikis planavimas, finansų struktūravimas, rimti pokalbiai, dvasinės praktikos.'
  }
};

// =====================================================
// MĖNESIO IR METŲ FONAI (10% + 10%)
// =====================================================
const MONTH_DATA = {
  1: {
    title: 'Naujų pradžių fonas',
    description: 'Mėnesio foninė energija subtiliai palaiko naujas iniciatyvas ir pirmąjį impulsą. Tai energinis sluoksnis, kuris neformuoja pagrindinės dienos krypties, tačiau švelniai skatina drąsiai pradėti ir pasitikėti savo vizija. Šis fonas prideda entuziazmo ir novatorišką atspalvį kasdieniniams veiksmams, net jei pagrindinė dienos energija yra visiškai kitokia.'
  },
  2: {
    title: 'Bendradarbiavimo fonas',
    description: 'Mėnesio foninė energija subtiliai palaiko santykius ir partnerystes. Tai energinis sluoksnis, kuris neformuoja pagrindinės dienos krypties, tačiau švelniai skatina diplomatiją, jautrumą ir bendrą darbą. Šis fonas prideda harmoniją ir supratimo atspalvį kasdieniniams veiksmams, net jei pagrindinė dienos energija yra visiškai kitokia.'
  },
  3: {
    title: 'Kūrybinis fonas',
    description: 'Mėnesio foninė energija subtiliai palaiko kūrybą ir saviraiškos džiaugsmą. Tai energinis sluoksnis, kuris neformuoja pagrindinės dienos krypties, tačiau švelniai skatina idėjų tekėjimą, komunikaciją ir optimizmą. Šis fonas prideda lengvumo ir spontaniškumo atspalvį kasdieniniams veiksmams, net jei pagrindinė dienos energija yra visiškai kitokia.'
  },
  4: {
    title: 'Stabilumo fonas',
    description: 'Mėnesio foninė energija subtiliai palaiko struktūrą ir tvarką. Tai energinis sluoksnis, kuris neformuoja pagrindinės dienos krypties, tačiau švelniai skatina planavimą, organizavimą ir pamatų statybą. Šis fonas prideda rimtumo ir patikimumo atspalvį kasdieniniams veiksmams, net jei pagrindinė dienos energija yra visiškai kitokia.'
  },
  5: {
    title: 'Pokyčių fonas',
    description: 'Mėnesio foninė energija subtiliai palaiko transformacijas ir judėjimą. Tai energinis sluoksnis, kuris neformuoja pagrindinės dienos krypties, tačiau švelniai skatina pokyčius, keliones ir naujų patirčių ieškojimą. Šis fonas prideda nuotykių dvasios ir lankstumą kasdieniniams veiksmams, net jei pagrindinė dienos energija yra visiškai kitokia.'
  },
  6: {
    title: 'Harmonijos fonas',
    description: 'Mėnesio foninė energija subtiliai palaiko meilę ir rūpestį. Tai energinis sluoksnis, kuris neformuoja pagrindinės dienos krypties, tačiau švelniai skatina santykių stiprinimą, namų jaukumą ir grožio kūrimą. Šis fonas prideda šilumos ir estetinio jautrumo atspalvį kasdieniniams veiksmams, net jei pagrindinė dienos energija yra visiškai kitokia.'
  },
  7: {
    title: 'Giluminio mąstymo fonas',
    description: 'Mėnesio foninė energija subtiliai palaiko refleksiją ir dvasingumą. Tai energinis sluoksnis, kuris neformuoja pagrindinės dienos krypties, tačiau švelniai skatina meditaciją, tyrimus ir vidinę kelionę. Šis fonas prideda filosofinio gilumo ir intuicijos atspalvį kasdieniniams veiksmams, net jei pagrindinė dienos energija yra visiškai kitokia.'
  },
  8: {
    title: 'Materialinio augimo fonas',
    description: 'Mėnesio foninė energija subtiliai palaiko verslą ir ambicijas. Tai energinis sluoksnis, kuris neformuoja pagrindinės dienos krypties, tačiau švelniai skatina karjeros žingsnius, investicijas ir materialinę sėkmę. Šis fonas prideda praktiškumo ir ilgalaikio mąstymo atspalvį kasdieniniams veiksmams, net jei pagrindinė dienos energija yra visiškai kitokia.'
  },
  9: {
    title: 'Užbaigimo fonas',
    description: 'Mėnesio foninė energija subtiliai palaiko užbaigimą ir atleidimą. Tai energinis sluoksnis, kuris neformuoja pagrindinės dienos krypties, tačiau švelniai skatina paleisti senus dalykus ir ruoštis naujiems ciklams. Šis fonas prideda išminties ir vidinės ramybės atspalvį kasdieniniams veiksmams, net jei pagrindinė dienos energija yra visiškai kitokia.'
  }
};

const YEAR_DATA = {
  1: {
    title: 'Naujų ciklų metai',
    description: 'Metų foninė energija primena, kad šis laikotarpis yra skirtas naujiems startams ir ilgalaikiams projektams. Tai ilgalaikis ciklas, kurio įtaka nuosekliai persmelkia visus mėnesius ir dienas – lyg vandenyno srovė, kuri nepastebimai, bet nuosekliai neša sėjimo ir pradėjimo link. Ši diena yra didesnio proceso dalis.'
  },
  2: {
    title: 'Santykių metai',
    description: 'Metų foninė energija primena, kad šis laikotarpis yra skirtas partnerystėms ir bendradarbiavimui. Tai ilgalaikis ciklas, kurio įtaka nuosekliai persmelkia visus mėnesius ir dienas – lyg vandenyno srovė, kuri nepastebimai, bet nuosekliai neša link kantrybės ir diplomatijos. Ši diena yra didesnio proceso dalis.'
  },
  3: {
    title: 'Kūrybos metai',
    description: 'Metų foninė energija primena, kad šis laikotarpis yra skirtas saviraiškos ir idėjų gimdymui. Tai ilgalaikis ciklas, kurio įtaka nuosekliai persmelkia visus mėnesius ir dienas – lyg vandenyno srovė, kuri nepastebimai, bet nuosekliai neša link kūrybinio impulso. Ši diena yra didesnio proceso dalis.'
  },
  4: {
    title: 'Pamatų statymo metai',
    description: 'Metų foninė energija primena, kad šis laikotarpis yra skirtas struktūros ir stabilumo kūrimui. Tai ilgalaikis ciklas, kurio įtaka nuosekliai persmelkia visus mėnesius ir dienas – lyg vandenyno srovė, kuri nepastebimai, bet nuosekliai neša link sunkaus, bet vaisingo darbo. Ši diena yra didesnio proceso dalis.'
  },
  5: {
    title: 'Transformacijos metai',
    description: 'Metų foninė energija primena, kad šis laikotarpis yra skirtas pokyčiams ir laisvei. Tai ilgalaikis ciklas, kurio įtaka nuosekliai persmelkia visus mėnesius ir dienas – lyg vandenyno srovė, kuri nepastebimai, bet nuosekliai neša link netikėtumų ir naujų galimybių. Ši diena yra didesnio proceso dalis.'
  },
  6: {
    title: 'Šeimos ir harmonijos metai',
    description: 'Metų foninė energija primena, kad šis laikotarpis yra skirtas santykiams ir namams. Tai ilgalaikis ciklas, kurio įtaka nuosekliai persmelkia visus mėnesius ir dienas – lyg vandenyno srovė, kuri nepastebimai, bet nuosekliai neša link meilės ir rūpesčio. Ši diena yra didesnio proceso dalis.'
  },
  7: {
    title: 'Dvasinės paieškos metai',
    description: 'Metų foninė energija primena, kad šis laikotarpis yra skirtas vidinei kelionei ir išminčiai. Tai ilgalaikis ciklas, kurio įtaka nuosekliai persmelkia visus mėnesius ir dienas – lyg vandenyno srovė, kuri nepastebimai, bet nuosekliai neša link refleksijos ir gilėjimo. Ši diena yra didesnio proceso dalis.'
  },
  8: {
    title: 'Materialinės sėkmės metai',
    description: 'Metų foninė energija primena, kad šis laikotarpis yra skirtas verslui ir ambicijoms. Tai ilgalaikis ciklas, kurio įtaka nuosekliai persmelkia visus mėnesius ir dienas – lyg vandenyno srovė, kuri nepastebimai, bet nuosekliai neša link derliaus nuėmimo ir sėkmės. Ši diena yra didesnio proceso dalis.'
  },
  9: {
    title: 'Užbaigimo ir išleidimo metai',
    description: 'Metų foninė energija primena, kad šis laikotarpis yra skirtas atleidimui ir ciklų užbaigimui. Tai ilgalaikis ciklas, kurio įtaka nuosekliai persmelkia visus mėnesius ir dienas – lyg vandenyno srovė, kuri nepastebimai, bet nuosekliai neša link pasiruošimo naujai pradžiai. Ši diena yra didesnio proceso dalis.'
  }
};

// Shadow sum narratives
const shadowNarratives = {
  1: 'ego impulsai, poreikis būti pastebėtam, nekantrumas',
  2: 'emocinė priklausomybė, per didelis jautrumas, neapsisprendimas',
  3: 'išsiblaškymas, paviršutiniškumas, sunkumas užbaigti',
  4: 'užsispyrimas, kontrolės siekimas, nelankstumas',
  5: 'nerimas, impulsyvumas, bėgimas nuo įsipareigojimų',
  6: 'perfekcionizmas, savęs aukojimas, per didelis rūpestis',
  7: 'atsiribojimas, pesimizmas, per didelė analizė',
  8: 'materializmas, kontrolės troškimas, dominavimas',
  9: 'sentimentalumas, prisirišimas prie praeities, idealizmas'
};

// =====================================================
// BENDROS SUVESTINĖS GENERATORIUS (PAGAL ETALONĄ)
// =====================================================
const generateUnifiedSummary = (dayNum, planet, monthNum, yearNum) => {
  const dayData = DAY_NUMBER_DATA[dayNum];
  const planetData = PLANET_FULL_DATA[planet.name];
  const monthData = MONTH_DATA[monthNum];
  const yearData = YEAR_DATA[yearNum];
  
  // Check if energies amplify each other
  const isAmplified = dayNum === planet.number;
  
  // Generate narrative according to new template
  let summary = `Pagrindinį impulsą suteikia dienos skaičius ${dayNum} – ${dayData.short.toLowerCase()} energija. Ji skatina ${getActionVerb(dayNum)}.\n\n`;
  
  summary += `Planetos režimą formuoja ${planet.genitive} energija, kuri šią dieną įneša ${planetData.short.toLowerCase().replace('spindesys ir charizma', 'spindesio ir charizmos').replace('intuicija ir emocijos', 'intuicijos ir emocijų').replace('veiksmas ir drąsa', 'veiksmo ir drąsos').replace('komunikacija ir greitis', 'komunikacijos ir greičio').replace('ekspansija ir sėkmė', 'ekspansijos ir sėkmės').replace('meilė ir grožis', 'meilės ir grožio').replace('disciplina ir branda', 'disciplinos ir brandos')} aspektą. ${planet.name} ${getPlanetNarrative(planet.name)}.\n\n`;
  
  if (isAmplified) {
    summary += `Kadangi dienos skaičius sutampa su planetos skaičiumi, šios energijos viena kitą sustiprina – diena yra ypač intensyvi ir koncentruota.\n\n`;
  }
  
  summary += `Šias dvi pagrindines energijas papildo mėnesio fonas, kuris suteikia ${monthData.title.toLowerCase().replace(' fonas', '')} atspalvį ir palaiko ${getMonthVerb(monthNum)}. Tai foninė energija, kuri subtiliai veikia bendrą atmosferą.\n\n`;
  
  summary += `Metų fonas įneša ilgalaikės krypties pojūtį – primena, kad ši diena yra didesnio proceso dalis. Tai nuoseklus judėjimas link ${getYearVerb(yearNum)}.\n\n`;
  
  summary += `Bendrai ši diena pasižymi ${getDayTheme(dayNum, planet.name)} temomis. Tai laikas, kai ${getDayConclusion(dayNum, planet.name)}.`;
  
  return summary;
};

// Helper functions for summary generation
const getActionVerb = (num) => {
  const verbs = {
    1: 'pradėti nauja ir vadovauti',
    2: 'bendradarbiauti ir kurti harmoniją',
    3: 'kurti ir reikšti save',
    4: 'struktūrizuoti ir statyti pamatus',
    5: 'keistis ir tyrinėti',
    6: 'rūpintis ir kurti grožį',
    7: 'reflektuoti ir gilintis',
    8: 'valdyti ir kurti materialinę vertę',
    9: 'apžvelgti nueitą kelią, paleisti tai, kas atliko savo funkciją, ir išlaikyti platesnį požiūrį'
  };
  return verbs[num] || '';
};

const getPlanetNarrative = (planet) => {
  const narratives = {
    'Saulė': 'išryškina tai, kas svarbiausia, padeda sutelkti dėmesį į vertybes ir kviečia veikti iš vidinio centro',
    'Mėnulis': 'atveria emocinį suvokimą, stiprina intuiciją ir ryšį su vidine išmintimi',
    'Marsas': 'aktyvuoja veiksmą, drąsą ir ryžtą, kviečia veikti, o ne laukti',
    'Merkurijus': 'aktyvuoja mintis, žodžius ir ryšius, skatina kalbėti, rašyti ir dalintis',
    'Jupiteris': 'aktyvuoja augimą ir optimizmą, skatina galvoti dideliu mastu ir plėstis',
    'Venera': 'aktyvuoja meilę, grožį ir harmoniją, kviečia kurti grožį ir mėgautis gyvenimu',
    'Saturnas': 'aktyvuoja discipliną ir atsakomybę, kviečia dirbti sistemingai ir kurti tvarų pagrindą'
  };
  return narratives[planet] || '';
};

const getPlanetVerb = (planet) => {
  const verbs = {
    'Saulė': 'individualumą ir gebėjimą spindėti',
    'Mėnulis': 'intuiciją ir emocinį suvokimą',
    'Marsas': 'veiksmą ir ryžtą',
    'Merkurijus': 'komunikaciją ir mąstymo greitį',
    'Jupiteris': 'augimą ir optimizmą',
    'Venera': 'meilę ir estetinį jautrumą',
    'Saturnas': 'discipliną ir ilgalaikį mąstymą'
  };
  return verbs[planet] || '';
};

const getMonthVerb = (num) => {
  const verbs = {
    1: 'iniciatyvą be skubėjimo',
    2: 'harmoniją santykiuose',
    3: 'kūrybinį impulsą',
    4: 'stabilumo paiešką',
    5: 'pokyčių priėmimą',
    6: 'rūpestį ir grožį',
    7: 'vidinę kelionę',
    8: 'materialinį augimą',
    9: 'senų ciklų paleidimą'
  };
  return verbs[num] || '';
};

const getYearVerb = (num) => {
  const verbs = {
    1: 'naujų pradžių',
    2: 'partnerystės ir bendradarbiavimo',
    3: 'kūrybinės saviraiškos',
    4: 'tvirto pagrindo sukūrimo',
    5: 'transformacijos ir laisvės',
    6: 'harmonijos ir meilės',
    7: 'dvasinės brandos',
    8: 'materialinės sėkmės',
    9: 'ciklo užbaigimo ir naujo etapo'
  };
  return verbs[num] || '';
};

const getDayTheme = (dayNum, planet) => {
  if (dayNum === 9) return 'užbaigimo ir sąmoningo perėjimo';
  if (dayNum === 1) return 'naujos pradžios ir lyderystės';
  if (dayNum === 7) return 'refleksijos ir vidinės paieškos';
  // Grąžina kilmininkų formą su kableliais: "partnerystės, jautrumo, veiksmo ir drąsos"
  const dayTheme = DAY_NUMBER_DATA[dayNum]?.theme || DAY_NUMBER_DATA[dayNum].short.toLowerCase();
  const planetTheme = PLANET_FULL_DATA[planet]?.theme || PLANET_FULL_DATA[planet].short.toLowerCase();
  return `${dayTheme}, ${planetTheme}`;
};

const getDayConclusion = (dayNum, planet) => {
  if (dayNum === 9) return 'patirtis tampa išmintimi, o aiškumas leidžia pasirinkti, ką verta neštis toliau, o ką palikti praeityje';
  if (dayNum === 1) return 'drąsa ir iniciatyva atveria naujus kelius';
  if (dayNum === 7) return 'tyla ir refleksija atveria gilesnį supratimą';
  return 'energijos susilieja ir sukuria unikalią dienos atmosferą';
};

// =====================================================
// INTERAKTYVUS LANGELIS KOMPONENTAS
// =====================================================
const InteractiveCell = ({ title, value, subtitle, color, isExpanded, onToggle, description, shadow, activities, cellType }) => {
  // cellType: 'primary' (40%) or 'background' (10%)
  const isPrimary = cellType === 'primary';
  
  return (
    <div className="relative h-full">
      <button
        onClick={onToggle}
        className={`w-full h-full rounded-lg p-4 text-center transition-all duration-200 cursor-pointer flex flex-col
          ${color} 
          ${isExpanded ? 'ring-2 ring-amber-400/50' : ''}
          hover:brightness-110
          ${isPrimary ? 'min-h-[140px] sm:min-h-[150px]' : 'min-h-[120px] sm:min-h-[130px]'}`}
      >
        {/* Viršutinis turinys - fiksuotas ties viršumi */}
        <div className="flex-shrink-0">
          <p className={`text-[10px] uppercase tracking-wider mb-1 ${color.includes('amber') ? 'text-amber-400/70' : color.includes('purple') ? 'text-purple-400/70' : color.includes('blue') ? 'text-blue-400/70' : 'text-indigo-400/70'}`}>
            {title}
          </p>
        </div>
        
        {/* Vidurinis turinys - centruotas */}
        <div className="flex-1 flex flex-col justify-center">
          <p className={`text-2xl font-display font-bold ${color.includes('amber') ? 'text-amber-400' : color.includes('purple') ? 'text-purple-400' : 'text-foreground/70'}`}>
            {value}
          </p>
          <p className={`text-xs mt-1 ${color.includes('amber') ? 'text-amber-400/80' : color.includes('purple') ? 'text-purple-400/80' : color.includes('blue') ? 'text-blue-400/60' : 'text-indigo-400/60'}`}>
            {subtitle}
          </p>
        </div>
        
        {/* Apatinis turinys - fiksuotas ties apačia */}
        <div className="flex-shrink-0">
          <p className="text-[9px] text-foreground/40">
            Spausti, kad skaityti plačiau
          </p>
        </div>
      </button>
      
      {isExpanded && (
        <div 
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ 
            zIndex: 9999, 
            backgroundColor: 'rgba(15, 15, 35, 0.98)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)'
          }}
          onClick={onToggle}
        >
          <div 
            className="w-full max-w-lg max-h-[80vh] overflow-y-auto p-5 rounded-lg border border-purple-500/30 text-left relative"
            style={{ backgroundColor: '#1a1a3e' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={onToggle}
              className="absolute top-3 right-3 text-foreground/50 hover:text-foreground/80 p-2 hover:bg-white/10 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
            <h4 className="text-amber-400 font-medium text-base mb-3 pr-8">{title}</h4>
            <p className="text-foreground/90 text-base leading-relaxed mb-4">{description}</p>
            {shadow && (
              <div className="mb-4">
                <p className="text-orange-400/80 text-sm font-medium mb-1">Šešėlis:</p>
                <p className="text-foreground/60 text-sm italic leading-relaxed">{shadow}</p>
              </div>
            )}
            {activities && (
              <div>
                <p className="text-green-400/80 text-sm font-medium mb-1">Palankios veiklos:</p>
                <p className="text-foreground/60 text-sm leading-relaxed">{activities}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// =====================================================
// PAGRINDINIS KOMPONENTAS
// =====================================================
export default function DailyEnergyAnalysis() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [expandedCell, setExpandedCell] = useState(null);
  const [isMainExpanded, setIsMainExpanded] = useState(false);
  const [showCalculation, setShowCalculation] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const analysis = useMemo(() => {
    const day = selectedDate.getDate();
    const month = selectedDate.getMonth() + 1;
    const year = selectedDate.getFullYear();
    const weekdayIndex = selectedDate.getDay();
    
    const dayNumber = calculateDayNumber(day);
    const monthNumber = calculateMonthNumber(month);
    const yearNumber = calculateYearNumber(year);
    const weekdayPlanet = getWeekdayPlanet(selectedDate);
    const shadowSum = calculateShadowSum(dayNumber, monthNumber, yearNumber, weekdayPlanet.number);
    const hasAmplified = dayNumber === weekdayPlanet.number;
    
    const unifiedSummary = generateUnifiedSummary(dayNumber, weekdayPlanet, monthNumber, yearNumber);
    
    return {
      day, month, year, weekdayIndex,
      dayNumber, monthNumber, yearNumber,
      weekdayPlanet, shadowSum, hasAmplified,
      unifiedSummary
    };
  }, [selectedDate]);

  const formatDateLT = (date) => {
    const day = date.getDate();
    const month = MONTHS_LT[date.getMonth()];
    const year = date.getFullYear();
    const weekday = WEEKDAYS_LT[date.getDay()];
    return `${year} m. ${month} ${day} d., ${weekday}`;
  };

  const toggleCell = (cellId) => {
    setExpandedCell(expandedCell === cellId ? null : cellId);
  };

  const dayData = DAY_NUMBER_DATA[analysis.dayNumber];
  const planetData = PLANET_FULL_DATA[analysis.weekdayPlanet.name];
  const monthData = MONTH_DATA[analysis.monthNumber];
  const yearData = YEAR_DATA[analysis.yearNumber];

  return (
    <Card className="bg-gradient-to-br from-indigo-950/80 via-violet-950/60 to-slate-950/80 border-indigo-500/30 p-6 sm:p-8">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center gap-3 mb-3">
          <Sun className="w-7 h-7 text-amber-400" />
          <h2 className="font-display text-2xl sm:text-3xl text-amber-400">
            Dienos energijos analizė
          </h2>
          <Moon className="w-7 h-7 text-indigo-400" />
        </div>
        <p className="text-foreground/70 text-sm sm:text-base">
          Kiekviena diena turi savo ritmą. Patikrinkite, su kokia energija ji ateina.
        </p>
      </div>

      {/* Date Picker */}
      <div className="bg-indigo-900/30 border border-indigo-500/20 rounded-lg p-4 mb-6">
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full sm:w-auto justify-start text-left font-normal bg-slate-900/50 border-indigo-500/30 hover:bg-slate-800/50 hover:border-amber-500/50">
                <CalendarIcon className="mr-2 h-4 w-4 text-indigo-400" />
                <span className="text-foreground">{format(selectedDate, 'yyyy-MM-dd')}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-slate-900 border-indigo-500/30" align="start">
              <LithuanianDatePicker
                selected={selectedDate}
                onSelect={(date) => { setSelectedDate(date); setCalendarOpen(false); }}
                onCancel={() => setCalendarOpen(false)}
                onClear={() => { setSelectedDate(new Date()); setCalendarOpen(false); }}
              />
            </PopoverContent>
          </Popover>
          <div className="text-center sm:text-left flex-1">
            <p className="text-foreground font-medium">{formatDateLT(selectedDate)}</p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-indigo-500/20">
          <p className="text-xs text-foreground/60 leading-relaxed">
            <span className="text-amber-400">ℹ️</span> Pasirinkite kalendorinę dieną ir pamatykite jos bendrą energetinį foną. 
            <strong className="text-foreground/80"> Ši analizė nėra asmeninė</strong> ir nėra susijusi su individualia gimimo data.
          </p>
        </div>
      </div>

      {/* ============================================= */}
      {/* 4 INTERAKTYVŪS LANGELIAI */}
      {/* ============================================= */}
      <div className="mb-6">
        {/* Pagrindinės energijos (40% + 40%) */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <InteractiveCell
            title="Dienos impulsas"
            value={analysis.dayNumber}
            subtitle={dayData.short}
            color="bg-amber-900/30 border-2 border-amber-500/40"
            isExpanded={expandedCell === 'day'}
            onToggle={() => toggleCell('day')}
            description={dayData.fullDescription}
            shadow={dayData.shadow}
            activities={dayData.activities}
            cellType="primary"
          />
          
          <InteractiveCell
            title="Planetos režimas"
            value={<><span className="text-xl">{analysis.weekdayPlanet.symbol}</span><br/><span className="text-sm">{analysis.weekdayPlanet.name}</span></>}
            subtitle={planetData.short}
            color="bg-purple-900/30 border-2 border-purple-500/40"
            isExpanded={expandedCell === 'planet'}
            onToggle={() => toggleCell('planet')}
            description={planetData.fullDescription}
            shadow={planetData.shadow}
            activities={planetData.activities}
            cellType="primary"
          />
        </div>
        
        {/* Foninės energijos (10% + 10%) */}
        <div className="grid grid-cols-2 gap-3">
          <InteractiveCell
            title="Mėnesio fonas"
            value={analysis.monthNumber}
            subtitle={monthData.title}
            color="bg-blue-900/20 border border-blue-500/20"
            isExpanded={expandedCell === 'month'}
            onToggle={() => toggleCell('month')}
            description={monthData.description}
            cellType="background"
          />
          
          <InteractiveCell
            title="Metų fonas"
            value={analysis.yearNumber}
            subtitle={yearData.title}
            color="bg-indigo-900/20 border border-indigo-500/20"
            isExpanded={expandedCell === 'year'}
            onToggle={() => toggleCell('year')}
            description={yearData.description}
            cellType="background"
          />
        </div>
      </div>

      {/* Amplified Mode Badge */}
      {analysis.hasAmplified && (
        <div className="bg-gradient-to-r from-amber-500/20 to-purple-500/20 border border-amber-500/40 rounded-lg p-3 mb-6 text-center">
          <div className="flex items-center justify-center gap-2 text-amber-400">
            <Zap className="w-4 h-4" />
            <span className="font-semibold text-sm">Sustiprintas režimas</span>
            <Zap className="w-4 h-4" />
          </div>
          <p className="text-xs text-foreground/70 mt-1">
            Dienos skaičius ({analysis.dayNumber}) sutampa su planetos skaičiumi – energija dvigubai intensyvi
          </p>
        </div>
      )}

      {/* ============================================= */}
      {/* BENDRA DIENOS APŽVALGA SU "SKAITYTI PLAČIAU" */}
      {/* ============================================= */}
      <div className="bg-gradient-to-r from-amber-500/10 to-purple-500/10 border border-amber-500/30 rounded-lg p-5 sm:p-6 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-amber-400" />
          <h3 className="font-display text-xl text-amber-400">Dienos energijų apžvalga</h3>
        </div>
        
        <div className="text-xs text-foreground/50 mb-4 space-y-0.5">
          <p>Dienos energija: {analysis.dayNumber}</p>
          <p>{WEEKDAYS_LT[analysis.weekdayIndex]} – {analysis.weekdayPlanet.genitive} ({analysis.weekdayPlanet.number}) energija</p>
        </div>
        
        {/* Sutrumpinta versija */}
        <p className="text-foreground/90 leading-relaxed">
          Ši diena formuojasi iš keturių tarpusavyje susijusių energinių sluoksnių, kurie kartu kuria savitą dienos ritmą ir kryptį.
        </p>
        
        {isMainExpanded && (
          <div 
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ 
              zIndex: 9999, 
              backgroundColor: 'rgba(15, 15, 35, 0.98)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)'
            }}
            onClick={() => setIsMainExpanded(false)}
          >
            <div 
              className="w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 rounded-lg border border-purple-500/30 text-left relative"
              style={{ backgroundColor: '#1a1a3e' }}
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                onClick={() => setIsMainExpanded(false)}
                className="absolute top-4 right-4 text-foreground/50 hover:text-foreground/80 p-2 hover:bg-white/10 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
              
              {/* Pilna suvestinė */}
              <div className="pr-8">
                <p className="text-foreground/80 text-base leading-relaxed whitespace-pre-line">
                  {analysis.unifiedSummary}
                </p>
              </div>
              
              {/* Pagrindinės įtakos */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div className="bg-amber-900/20 rounded-lg p-4">
                  <p className="text-amber-400 text-sm font-medium mb-2">Dienos impulsas ({analysis.dayNumber})</p>
                  <p className="text-foreground/70 text-base leading-relaxed">{dayData.fullDescription}</p>
                </div>
                <div className="bg-purple-900/20 rounded-lg p-4">
                  <p className="text-purple-400 text-sm font-medium mb-2">{analysis.weekdayPlanet.genitive} režimas</p>
                  <p className="text-foreground/70 text-base leading-relaxed">{planetData.fullDescription}</p>
                </div>
              </div>
              
              {/* Šešėliniai poveikiai */}
              <div className="bg-orange-900/10 border border-orange-500/20 rounded-lg p-5 mt-4">
                <p className="text-orange-400/90 text-sm font-medium mb-3">🌒 Šešėlinė nata (jei taikoma):</p>
                <p className="text-foreground/70 text-base leading-relaxed mb-3">
                  Papildomai gali pasireikšti subtilus foninis impulsas, susijęs su šešėline suma ({analysis.shadowSum}). Jis gali išryškėti kaip {shadowNarratives[analysis.shadowSum]}.
                </p>
                <p className="text-foreground/50 text-sm italic">
                  Tai nėra pagrindinė dienos energija, o tik foninis atspalvis, padedantis geriau suvokti bendrą dienos dinamiką.
                </p>
              </div>
              
              {/* Praktinės rekomendacijos */}
              <div className="bg-green-900/10 border border-green-500/20 rounded-lg p-5 mt-4">
                <p className="text-green-400/90 text-sm font-medium mb-3">Kaip bendradarbiauti su šios dienos energija:</p>
                <ul className="text-foreground/70 text-base space-y-2 leading-relaxed">
                  <li>• Išnaudokite dienos impulsą: {dayData.activities.toLowerCase()}</li>
                  <li>• Derinkite su {analysis.weekdayPlanet.genitive.toLowerCase()} energija: {planetData.activities.toLowerCase()}</li>
                  <li>• Stebėkite šešėlinius pasireiškimus ir sąmoningai juos balansuokite</li>
                </ul>
              </div>
              
              {/* Close button */}
              <div className="mt-4">
                <Button 
                  variant="ghost" 
                  onClick={() => setIsMainExpanded(false)} 
                  className="w-full text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                >
                  <ChevronUp className="w-4 h-4 mr-2" /> Suskleisti
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {!isMainExpanded && (
          <Button 
            variant="ghost" 
            onClick={() => setIsMainExpanded(true)} 
            className="w-full mt-4 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
          >
            <ChevronDown className="w-4 h-4 mr-2" /> Skaityti plačiau
          </Button>
        )}
      </div>

      {/* ============================================= */}
      {/* ŠEŠĖLINĖ SUMA */}
      {/* ============================================= */}
      <div className="bg-slate-900/50 border border-slate-700/30 rounded-lg p-4 mb-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-slate-400 font-medium mb-1">
              Šešėlinė nata <span className="text-amber-400/80 ml-1">{analysis.shadowSum}</span>
            </p>
            <p className="text-sm text-foreground/60 leading-relaxed">
              Foninis impulsas gali pasireikšti kaip: {shadowNarratives[analysis.shadowSum]}.
            </p>
            <p className="text-xs text-foreground/40 mt-2 italic">
              Tai ne pagrindinė energija, o subtilus šešėlinis atspalvis.
            </p>
          </div>
        </div>
      </div>

      {/* Calculation Info */}
      <div className="mt-4">
        <Button variant="ghost" size="sm" onClick={() => setShowCalculation(!showCalculation)} className="text-foreground/50 hover:text-foreground/80 text-xs">
          <Info className="w-3 h-3 mr-1" />
          {showCalculation ? 'Slėpti skaičiavimą' : 'Kaip apskaičiuota?'}
        </Button>
        
        {showCalculation && (
          <div className="mt-3 bg-slate-900/50 border border-slate-700/30 rounded-lg p-4 text-xs text-foreground/70 space-y-2">
            <p className="text-foreground/90 font-medium mb-2">Metodinė struktūra:</p>
            <div className="bg-indigo-900/20 rounded p-2 mb-3 text-[11px]">
              <p className="text-indigo-400 font-medium mb-1">Svoriai (nesumuojami tarpusavyje):</p>
              <p>• <span className="text-amber-400">Dienos impulsas</span> – pagrindinis (40%)</p>
              <p>• <span className="text-purple-400">Planetos režimas</span> – pagrindinis (40%)</p>
              <p>• <span className="text-blue-400">Mėnesio fonas</span> – foninis (10%)</p>
              <p>• <span className="text-indigo-400">Metų fonas</span> – foninis (10%)</p>
            </div>
            <p><strong className="text-amber-400">Dienos impulsas:</strong> {analysis.day} → {analysis.day > 9 ? `${String(analysis.day).split('').join(' + ')} = ` : ''}{analysis.dayNumber}</p>
            <p><strong className="text-purple-400">Planetos režimas:</strong> {WEEKDAYS_LT[analysis.weekdayIndex]} → {analysis.weekdayPlanet.name} {analysis.weekdayPlanet.symbol} (skaičius {analysis.weekdayPlanet.number})</p>
            <p><strong className="text-blue-400">Mėnesio fonas:</strong> {MONTHS_LT[analysis.month - 1]} ({analysis.month}) → {analysis.monthNumber}</p>
            <p><strong className="text-indigo-400">Metų fonas:</strong> {analysis.year} → {String(analysis.year).split('').join(' + ')} = {String(analysis.year).split('').reduce((s, d) => s + parseInt(d), 0)} → {analysis.yearNumber}</p>
            <p className="pt-2 border-t border-slate-700/30">
              <strong className="text-slate-400">Šešėlinė suma:</strong> {analysis.dayNumber} + {analysis.monthNumber} + {analysis.yearNumber} + {analysis.weekdayPlanet.number} = {analysis.dayNumber + analysis.monthNumber + analysis.yearNumber + analysis.weekdayPlanet.number}
              {(analysis.dayNumber + analysis.monthNumber + analysis.yearNumber + analysis.weekdayPlanet.number) > 9 && <span> → {analysis.shadowSum}</span>}
              <span className="text-foreground/50"> (tik foninis impulsas, NIEKADA pagrindinė energija)</span>
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}
